import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getContractValueLog,
  getContractValueSummary,
  createManualAdjustment,
  getChangeOrders,
  createChangeOrder,
  updateChangeOrder,
  deleteChangeOrder,
  getSOVProjectChangeLog,
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import LoadingState from './LoadingState';
import useConfirm from '../hooks/useConfirm';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';
import SegmentedControl from './ui/SegmentedControl';

/* ═══════════════════════════════════════════════════════════════════════════
   Contract Value tab — warm-cream + sage redesign
   Tracks contract value over time: original contract, change orders, manual
   adjustments, contract-value audit log + SOV line-item edit log.
   Token-only colors. Visual reskin — workflow + API unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─────────────── Formatters ─────────────── */

const fmt$ = (n, { signed = false, cents = true } = {}) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(+n)) return '—';
  const num = +n;
  const abs = Math.abs(num);
  const s = abs.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
  if (num < 0) return '-' + s;
  if (signed && num > 0) return '+' + s;
  return s;
};

// approved_date / submitted_date are TEXT: either a calendar day ('2026-08-14'), which
// new Date() reads as UTC midnight and so shows the day before in Central time, or a
// Postgres timestamp ('2026-08-14 15:00:00.123456+00'), made ISO here so every browser
// parses it (Safari rejects the space form).
const toDate = (d) => {
  if (!d) return null;
  if (d instanceof Date) return Number.isNaN(d.getTime()) ? null : d;
  const s = String(d).trim();
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (day) return new Date(+day[1], +day[2] - 1, +day[3]);
  const dt = new Date(s.replace(/^(\d{4}-\d{2}-\d{2}) (?=\d)/, '$1T').replace(/(\.\d{3})\d+/, '$1').replace(/([+-]\d{2})$/, '$1:00'));
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const fmtDate = (d) => {
  const dt = toDate(d);
  if (!dt) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtDateTime = (d) => {
  const dt = toDate(d);
  if (!dt) return '—';
  return (
    dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' +
    dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
};

/* ─────────────── Inline SVG icons (matches Icon.js vocabulary) ─────────────── */

const Icon = ({ name, size = 14, sw = 1.6 }) => {
  const s = size;
  const p = (d) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      {d}
    </svg>
  );
  switch (name) {
    case 'plus':
      return p(<path d="M12 5v14M5 12h14" />);
    case 'x':
      return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'check':
      return p(<path d="m5 12 5 5L20 7" />);
    case 'sliders':
      return p(
        <>
          <path d="M4 6h10M18 6h2" />
          <path d="M4 12h2M10 12h10" />
          <path d="M4 18h12M20 18h0" />
          <circle cx="16" cy="6" r="2" />
          <circle cx="8" cy="12" r="2" />
          <circle cx="18" cy="18" r="2" />
        </>
      );
    case 'contract':
      return p(
        <>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M9 13h6M9 17h4" />
        </>
      );
    case 'warn-tri':
      return p(
        <>
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          <path d="M12 9v4M12 17h.01" />
        </>
      );
    case 'history':
      return p(
        <>
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
          <path d="M3 3v5h5" />
          <path d="M12 8v4l3 2" />
        </>
      );
    case 'list':
      return p(
        <>
          <path d="M8 6h13M8 12h13M8 18h13" />
          <path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
        </>
      );
    case 'send':
      return p(<path d="M11 13 22 2 15 22l-4-9-9-4Z" />);
    case 'trash':
      return p(
        <>
          <path d="M4 7h16M10 11v6M14 11v6" />
          <path d="M5 7l1 13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-13" />
          <path d="M9 7V4h6v3" />
        </>
      );
    case 'clock':
      return p(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4l3 2" />
        </>
      );
    case 'spin':
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={sw}
          strokeLinecap="round"
          style={{ display: 'block', animation: 'cvSpin .8s linear infinite' }}
        >
          <path d="M21 12a9 9 0 1 1-6.2-8.55" />
        </svg>
      );
    default:
      return null;
  }
};

/* ─────────────── Status + type config ─────────────── */

// Real API stores CO status capitalized; key the config by lowercase.
const CO_STATUS = {
  draft: { label: 'Draft', tone: 'var(--text-3)', soft: 'var(--bg-surface-2)', icon: 'clock', neutral: true },
  submitted: { label: 'Submitted', tone: 'var(--warning)', soft: 'var(--warning-soft)', icon: 'send' },
  approved: { label: 'Approved', tone: 'var(--success)', soft: 'var(--success-soft)', icon: 'check' },
  rejected: { label: 'Rejected', tone: 'var(--danger)', soft: 'var(--danger-soft)', icon: 'x' },
};

// Until change orders run through Summit, CE's change-order list sets the approved total
// nightly (server/jobs/ceChangeOrderContractSync.js). The CE part = that total minus the
// change orders entered in Summit; under $1 is REAL rounding, not a change order.
const ceChangeOrdersPart = (summary, approvedCOs) =>
  (+summary.approved_change_orders_total || 0) - approvedCOs.reduce((a, co) => a + (+co.approved_amount || 0), 0);
const hasCePart = (amount) => Math.abs(amount) >= 1;

// Real API change_type strings → tone config.
const AUDIT_TYPE = {
  'Original Contract': { label: 'Original Contract', tone: 'var(--success)', soft: 'var(--success-soft)' },
  'Change Order': { label: 'Change Order', tone: 'var(--accent-text)', soft: 'var(--accent-soft)' },
  'CE Change Orders': { label: 'CE Change Orders', tone: 'var(--accent-text)', soft: 'var(--accent-soft)' },
  'Manual Adjustment': { label: 'Manual Adjustment', tone: 'var(--warning)', soft: 'var(--warning-soft)' },
};

const StatusBadge = ({ status }) => {
  const c = CO_STATUS[String(status || '').toLowerCase()] || CO_STATUS.draft;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 9px 2px 7px',
        borderRadius: 999,
        fontSize: 12.5,
        fontWeight: 500,
        color: c.tone,
        background: c.soft,
        border: c.neutral
          ? '1px solid var(--border-default)'
          : `1px solid color-mix(in oklch, ${c.tone} 22%, transparent)`,
        letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon name={c.icon} size={11} sw={2} /> {c.label}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const c = AUDIT_TYPE[type] || { label: type || 'Adjustment', tone: 'var(--warning)', soft: 'var(--warning-soft)' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 9px',
        borderRadius: 999,
        fontSize: 12.5,
        fontWeight: 500,
        color: c.tone,
        background: c.soft,
        border: `1px solid color-mix(in oklch, ${c.tone} 22%, transparent)`,
        letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
      }}
    >
      {c.label}
    </span>
  );
};

/* ─────────────── Buttons ─────────────── */

const bPrimary = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 32,
  padding: '0 13px',
  borderRadius: 7,
  fontSize: 14,
  fontWeight: 500,
  color: '#fff',
  background: 'var(--accent)',
  border: '1px solid color-mix(in oklch, var(--accent) 80%, oklch(0.2 0.02 135))',
  boxShadow:
    'inset 0 1px 0 oklch(1 0 0 / 0.18), 0 1px 0 color-mix(in oklch, var(--accent) 70%, oklch(0.25 0.04 135))',
  cursor: 'pointer',
};
const bGhost = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 32,
  padding: '0 12px',
  borderRadius: 7,
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--text-2)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  boxShadow: 'var(--shadow-xs)',
  cursor: 'pointer',
};

/* ─────────────── Section header ─────────────── */

const SectionHeader = ({ onManualAdjust, onNewCO }) => (
  <header style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '0 2px' }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
      <h1 style={{ margin: 0, fontSize: 20.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
        Contract Value
      </h1>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--text-3)', letterSpacing: '-0.005em' }}>
        Track contract value changes, change orders, and manual adjustments.
      </p>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
      <button type="button" className="cv-ghost-btn" onClick={onManualAdjust} style={bGhost}>
        <Icon name="sliders" size={14} /> Manual Adjustment
      </button>
      <button type="button" className="cv-primary-btn" onClick={onNewCO} style={bPrimary}>
        <Icon name="plus" size={14} sw={2.2} /> Change Order
      </button>
    </div>
  </header>
);

/* ─────────────── KPI row ─────────────── */

const KpiTile = ({ label, value, valueColor, sub, fill, ring, muted = false }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      flex: '1 1 160px',
      minWidth: 160,
      padding: '18px 20px',
      borderRadius: 10,
      background: fill || 'transparent',
      border: ring ? `1px solid ${ring}` : '1px solid transparent',
    }}
  >
    <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
      {label}
    </div>
    <div
      className="mono"
      style={{
        fontSize: 23.5,
        fontWeight: 700,
        color: muted ? 'var(--text-4)' : valueColor || 'var(--text-1)',
        letterSpacing: '-0.025em',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.1,
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: 13.5, color: 'var(--text-3)' }}>{sub}</div>
  </div>
);

const KpiDivider = () => (
  <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '8px 0', flex: '0 0 auto' }} />
);

const KpiRow = ({ summary, approvedCOs, approvedCount, pendingCount, pendingTotal }) => {
  const approvedTotal = +summary.approved_change_orders_total || 0;
  const fromCe = hasCePart(ceChangeOrdersPart(summary, approvedCOs));
  const approvedZero = approvedTotal === 0;
  const pendingZero = pendingCount === 0;
  return (
    <section
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        padding: 6,
        gap: 0,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <KpiTile label="Original Contract" value={fmt$(summary.original_contract_value)} sub="base value" />
      <KpiDivider />
      <KpiTile
        label="Approved COs"
        value={fmt$(approvedTotal, { signed: !approvedZero })}
        valueColor="var(--accent-text)"
        sub={fromCe ? (approvedCount ? `${approvedCount} approved + from CE` : 'from CE') : `${approvedCount} approved`}
        muted={approvedZero}
        fill={approvedZero ? null : 'var(--accent-soft)'}
        ring={approvedZero ? null : 'color-mix(in oklch, var(--accent) 22%, transparent)'}
      />
      <KpiDivider />
      <KpiTile
        label="Pending COs"
        value={fmt$(pendingTotal)}
        valueColor="var(--warning)"
        sub={`${pendingCount} pending`}
        muted={pendingZero}
        fill={pendingZero ? null : 'var(--warning-soft)'}
        ring={pendingZero ? null : 'color-mix(in oklch, var(--warning) 22%, transparent)'}
      />
      <KpiDivider />
      <KpiTile
        label="Current Contract Value"
        value={fmt$(summary.revised_contract_value)}
        valueColor="var(--success)"
        sub="revised total"
        fill="var(--success-soft)"
        ring="color-mix(in oklch, var(--success) 22%, transparent)"
      />
    </section>
  );
};

/* ─────────────── State screen (inline — needs custom icons + sage CTA) ─────────────── */

const StateScreen = ({ icon = 'contract', title, body, ctaLabel, onCta }) => (
  <section
    style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-default)',
      borderRadius: 12,
      boxShadow: 'var(--shadow-sm)',
      padding: '56px 24px 60px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
      textAlign: 'center',
    }}
  >
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: 14,
        background: 'var(--accent-soft)',
        border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
        color: 'var(--accent-text)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Icon name={icon} size={24} sw={1.7} />
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
      <h3 style={{ margin: 0, fontSize: 17.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>{title}</h3>
      <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-3)', lineHeight: 1.65 }}>{body}</p>
    </div>
    {ctaLabel && (
      <button
        type="button"
        onClick={onCta}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 32,
          padding: '0 14px',
          borderRadius: 999,
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--accent-text)',
          background: 'var(--accent-soft)',
          border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
          cursor: 'pointer',
        }}
      >
        <Icon name="plus" size={13} sw={2.4} /> {ctaLabel}
      </button>
    )}
  </section>
);

/* ─────────────── Overview ledger ─────────────── */

const LedgerRow = ({ children, style }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '13px 24px',
      borderBottom: '1px solid var(--border-subtle)',
      ...style,
    }}
  >
    {children}
  </div>
);

const Overview = ({ summary, approvedCOs }) => {
  const mismatch = +summary.sov_total !== +summary.revised_contract_value;
  const diff = (+summary.sov_total || 0) - (+summary.revised_contract_value || 0);
  const manualTotal = +summary.manual_adjustments_total || 0;
  // The CE part not entered as a Summit change order shows as one row, so the breakdown adds up.
  const ceCOs = ceChangeOrdersPart(summary, approvedCOs);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '12px 24px',
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border-subtle)',
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--text-3)',
          }}
        >
          Contract Value Breakdown
        </div>

        {/* Original */}
        <LedgerRow>
          <span style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-1)' }}>Original Contract Value</span>
          <div style={{ flex: 1 }} />
          <span className="mono" style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>
            {fmt$(summary.original_contract_value)}
          </span>
        </LedgerRow>

        {/* Approved COs */}
        {approvedCOs.map((co) => {
          const amt = +co.approved_amount || 0;
          const positive = amt >= 0;
          return (
            <LedgerRow key={co.id}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 18,
                  height: 18,
                  flex: '0 0 auto',
                  color: positive ? 'var(--accent-text)' : 'var(--danger)',
                }}
              >
                <Icon name={positive ? 'plus' : 'x'} size={12} sw={2.4} />
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                <span style={{ fontSize: 15, color: 'var(--text-2)', letterSpacing: '-0.005em' }}>
                  <span className="mono" style={{ color: 'var(--accent-text)', fontWeight: 500 }}>
                    CO {co.change_order_number}
                  </span>
                  <span style={{ color: 'var(--text-4)', margin: '0 6px' }}>·</span>
                  {co.title}
                </span>
                {(co.approved_date || co.submitted_date || co.created_at) && (
                  <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
                    approved {fmtDate(co.approved_date || co.submitted_date || co.created_at)}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }} />
              <span
                className="mono"
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: positive ? 'var(--accent-text)' : 'var(--danger)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmt$(amt, { signed: true })}
              </span>
            </LedgerRow>
          );
        })}

        {/* Change orders from CE */}
        {hasCePart(ceCOs) && (
          <LedgerRow>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                flex: '0 0 auto',
                color: ceCOs >= 0 ? 'var(--accent-text)' : 'var(--danger)',
              }}
            >
              <Icon name={ceCOs >= 0 ? 'plus' : 'x'} size={12} sw={2.4} />
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
              <span style={{ fontSize: 15, color: 'var(--text-2)', letterSpacing: '-0.005em' }}>Change orders from CE</span>
              <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>kept in sync with CE nightly</span>
            </div>
            <div style={{ flex: 1 }} />
            <span
              className="mono"
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: ceCOs >= 0 ? 'var(--accent-text)' : 'var(--danger)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {fmt$(ceCOs, { signed: true })}
            </span>
          </LedgerRow>
        )}

        {/* Manual adjustments */}
        {manualTotal !== 0 && (
          <LedgerRow>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                flex: '0 0 auto',
                color: 'var(--warning)',
              }}
            >
              <Icon name="sliders" size={12} sw={1.8} />
            </span>
            <span style={{ fontSize: 15, color: 'var(--text-2)' }}>Manual Adjustments</span>
            <div style={{ flex: 1 }} />
            <span className="mono" style={{ fontSize: 15, fontWeight: 600, color: 'var(--warning)', fontVariantNumeric: 'tabular-nums' }}>
              {fmt$(manualTotal, { signed: true })}
            </span>
          </LedgerRow>
        )}

        {/* Total */}
        <LedgerRow style={{ borderBottom: 'none', background: 'var(--bg-surface-2)' }}>
          <span style={{ fontSize: 16.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>
            Current Contract Value
          </span>
          <div style={{ flex: 1 }} />
          <span
            className="mono"
            style={{ fontSize: 19.5, fontWeight: 700, color: 'var(--success)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}
          >
            {fmt$(summary.revised_contract_value)}
          </span>
        </LedgerRow>
      </section>

      {/* SOV mismatch warning */}
      {mismatch && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '14px 18px',
            borderRadius: 10,
            background: 'var(--warning-soft)',
            boxShadow: 'inset 3px 0 0 var(--warning)',
          }}
        >
          <span style={{ color: 'var(--warning)', flex: '0 0 auto', marginTop: 1 }}>
            <Icon name="warn-tri" size={16} sw={1.8} />
          </span>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.55 }}>
            SOV total (
            <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmt$(summary.sov_total)}
            </span>
            ) does not match current contract value (
            <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmt$(summary.revised_contract_value)}
            </span>
            ). Difference:{' '}
            <span className="mono" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--text-1)' }}>
              {fmt$(diff, { signed: true })}
            </span>
            .
          </p>
        </div>
      )}
    </div>
  );
};

/* ─────────────── Shared table chrome ─────────────── */

const TableCard = ({ template, head, children, footer }) => (
  <section
    style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-default)',
      borderRadius: 12,
      boxShadow: 'var(--shadow-sm)',
      overflow: 'hidden',
    }}
  >
    <div style={{ overflow: 'auto', position: 'relative' }}>
      <div style={{ minWidth: 'fit-content' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: template,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            minHeight: 40,
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          {head.map((h, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: h.align === 'right' ? 'flex-end' : h.align === 'center' ? 'center' : 'flex-start',
                padding: '0 16px',
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: 'var(--text-3)',
              }}
            >
              {h.label}
            </div>
          ))}
        </div>
        {children}
        {footer}
      </div>
    </div>
  </section>
);

const Cell = ({ children, align = 'left', style }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
      padding: '0 16px',
      minWidth: 0,
      ...style,
    }}
  >
    {children}
  </div>
);

/* ─────────────── CO workflow actions ─────────────── */

const actBtn = (tone, soft, busy, disabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  height: 28,
  padding: '0 10px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
  color: tone,
  background: soft,
  border: `1px solid color-mix(in oklch, ${tone} 22%, transparent)`,
  whiteSpace: 'nowrap',
  opacity: disabled ? 0.45 : 1,
  cursor: disabled || busy ? 'default' : 'pointer',
});

const RowAction = ({ icon, label, tone, soft, busy, disabled, onClick }) => (
  <button type="button" onClick={onClick} disabled={disabled || busy} style={actBtn(tone, soft, busy, disabled)}>
    {busy ? <Icon name="spin" size={12} sw={2.2} /> : <Icon name={icon} size={12} sw={2} />}
    {label}
  </button>
);

const StaticLabel = ({ children }) => <span style={{ fontSize: 13, color: 'var(--text-3)' }}>{children}</span>;

const COActions = ({ co, statusChangingId, deletingId, onSubmit, onDelete, onApprove, onReject }) => {
  const status = String(co.status || '').toLowerCase();
  const submitting = statusChangingId === co.id;
  const deleting = deletingId === co.id;
  if (status === 'draft') {
    return (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <RowAction icon="send" label="Submit" tone="var(--warning)" soft="var(--warning-soft)" busy={submitting} disabled={deleting} onClick={() => onSubmit(co)} />
        <RowAction icon="trash" label="Delete" tone="var(--danger)" soft="var(--danger-soft)" busy={deleting} disabled={submitting} onClick={() => onDelete(co)} />
      </div>
    );
  }
  if (status === 'submitted') {
    return (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <RowAction icon="check" label="Approve" tone="var(--success)" soft="var(--success-soft)" busy={submitting} disabled={false} onClick={() => onApprove(co)} />
        <RowAction icon="x" label="Reject" tone="var(--danger)" soft="var(--danger-soft)" busy={false} disabled={submitting} onClick={() => onReject(co)} />
      </div>
    );
  }
  if (status === 'approved') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <StaticLabel>Added to SOV</StaticLabel>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <StaticLabel>Closed</StaticLabel>
    </div>
  );
};

const CO_TEMPLATE = '88px minmax(280px, 1fr) 128px 130px 130px 116px 220px';

const ChangeOrdersTable = ({ changeOrders, onNewCO, statusChangingId, deletingId, onSubmit, onDelete, onApprove, onReject }) => {
  // The top bar's search (lib/pageSearch) — each list section has its own; the Overview ledger has
  // none. The footer totals the rows shown; the KPI row above stays the whole contract's.
  const search = usePageSearch('Search change orders…');
  if (!changeOrders.length) {
    return (
      <StateScreen
        icon="contract"
        title="No change orders yet"
        body="Create a change order that will affect the contract value when approved."
        ctaLabel="Change Order"
        onCta={onNewCO}
      />
    );
  }

  const shown = changeOrders.filter((co) => matchesSearch(search, co.change_order_number, co.title, co.description,
    (CO_STATUS[String(co.status || '').toLowerCase()] || CO_STATUS.draft).label));
  const sumReq = shown.reduce((a, co) => a + (+co.requested_amount || 0), 0);
  const sumApp = shown.reduce((a, co) => a + (+co.approved_amount || 0), 0);

  const head = [
    { label: 'CO #' },
    { label: 'Title' },
    { label: 'Status' },
    { label: 'Requested', align: 'right' },
    { label: 'Approved', align: 'right' },
    { label: 'Date' },
    { label: 'Actions', align: 'right' },
  ];

  const footer = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: CO_TEMPLATE,
        position: 'sticky',
        bottom: 0,
        zIndex: 3,
        minHeight: 48,
        background: 'var(--bg-surface-2)',
        borderTop: '1px solid var(--border-default)',
      }}
    >
      <div
        style={{
          gridColumn: '1 / 3',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: 'var(--text-2)',
        }}
      >
        Totals · {shown.length < changeOrders.length ? `${shown.length} of ${changeOrders.length}` : shown.length} change orders
      </div>
      <div />
      <Cell align="right">
        <span className="mono" style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>
          {fmt$(sumReq, { signed: true })}
        </span>
      </Cell>
      <Cell align="right">
        <span className="mono" style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--success)', fontVariantNumeric: 'tabular-nums' }}>
          {fmt$(sumApp, { signed: true })}
        </span>
      </Cell>
      <div />
      <div />
    </div>
  );

  return (
    <TableCard template={CO_TEMPLATE} head={head} footer={footer}>
      {shown.length === 0 && <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 14, color: 'var(--text-3)' }}>{noMatchText(search).body}</div>}
      {shown.map((co) => {
        const busy = statusChangingId === co.id || deletingId === co.id;
        const requested = +co.requested_amount || 0;
        const approvedPending = co.approved_amount === null || co.approved_amount === undefined || co.approved_amount === '';
        const approved = +co.approved_amount || 0;
        return (
          <div
            key={co.id}
            className="cv-row"
            style={{
              display: 'grid',
              gridTemplateColumns: CO_TEMPLATE,
              minHeight: 56,
              borderBottom: '1px solid var(--border-subtle)',
              opacity: busy ? 0.85 : 1,
            }}
          >
            <Cell>
              <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: 'var(--accent-text)', fontVariantNumeric: 'tabular-nums' }}>
                {co.change_order_number}
              </span>
            </Cell>
            <Cell>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, paddingRight: 12 }}>
                <span style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {co.title}
                </span>
                {co.description && (
                  <span style={{ fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {co.description}
                  </span>
                )}
              </div>
            </Cell>
            <Cell>
              <StatusBadge status={co.status} />
            </Cell>
            <Cell align="right">
              <span className="mono" style={{ fontSize: 14, color: requested < 0 ? 'var(--danger)' : 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>
                {fmt$(requested, { signed: true })}
              </span>
            </Cell>
            <Cell align="right">
              {approvedPending ? (
                <span className="mono" style={{ fontSize: 14, color: 'var(--text-4)' }}>
                  —
                </span>
              ) : (
                <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: approved < 0 ? 'var(--danger)' : 'var(--success)', fontVariantNumeric: 'tabular-nums' }}>
                  {fmt$(approved, { signed: true })}
                </span>
              )}
            </Cell>
            <Cell>
              <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
                {fmtDate(co.approved_date || co.submitted_date || co.created_at)}
              </span>
            </Cell>
            <Cell align="right">
              <COActions
                co={co}
                statusChangingId={statusChangingId}
                deletingId={deletingId}
                onSubmit={onSubmit}
                onDelete={onDelete}
                onApprove={onApprove}
                onReject={onReject}
              />
            </Cell>
          </div>
        );
      })}
    </TableCard>
  );
};

/* ─────────────── Audit Log ─────────────── */

const AUDIT_TEMPLATE = '170px minmax(300px, 1fr) 160px 150px 140px 150px';

const AuditLogTable = ({ entries }) => {
  const search = usePageSearch('Search the audit log…');
  if (!entries.length) {
    return (
      <StateScreen
        icon="history"
        title="No contract value changes recorded yet"
        body="Approve a change order or record a manual adjustment, and every contract-value change will be logged here."
      />
    );
  }
  const head = [
    { label: 'Date' },
    { label: 'Description' },
    { label: 'Type' },
    { label: 'Before', align: 'right' },
    { label: 'Change', align: 'right' },
    { label: 'After', align: 'right' },
  ];
  const shownEntries = entries.filter((e) => matchesSearch(search, e.description, e.changed_by, e.change_type || 'Adjustment'));
  return (
    <TableCard template={AUDIT_TEMPLATE} head={head}>
      {shownEntries.length === 0 && <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 14, color: 'var(--text-3)' }}>{noMatchText(search).body}</div>}
      {shownEntries.map((e) => {
        const change = +e.amount_change || 0;
        return (
          <div
            key={e.id}
            className="cv-row"
            style={{ display: 'grid', gridTemplateColumns: AUDIT_TEMPLATE, minHeight: 56, borderBottom: '1px solid var(--border-subtle)' }}
          >
            <Cell>
              <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
                {fmtDateTime(e.created_at)}
              </span>
            </Cell>
            <Cell>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, paddingRight: 12 }}>
                <span style={{ fontSize: 14.5, color: 'var(--text-1)', letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.description}
                </span>
                {e.changed_by && <span style={{ fontSize: 13, color: 'var(--text-3)' }}>by {e.changed_by}</span>}
              </div>
            </Cell>
            <Cell>
              <TypeBadge type={e.change_type} />
            </Cell>
            <Cell align="right">
              <span className="mono" style={{ fontSize: 14, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
                {fmt$(e.amount_before)}
              </span>
            </Cell>
            <Cell align="right">
              <span className="mono" style={{ fontSize: 14, fontWeight: 600, color: change < 0 ? 'var(--danger)' : 'var(--success)', fontVariantNumeric: 'tabular-nums' }}>
                {fmt$(change, { signed: true })}
              </span>
            </Cell>
            <Cell align="right">
              <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>
                {fmt$(e.amount_after)}
              </span>
            </Cell>
          </div>
        );
      })}
    </TableCard>
  );
};

/* ─────────────── SOV Changes ─────────────── */

const SOV_TEMPLATE = '128px 96px minmax(220px, 1fr) 168px 150px 150px minmax(170px, 0.7fr)';

const isMoneyField = (f) => f === 'contract_amount';
const prettyField = (f) => String(f || '').replace(/_/g, '  ');
const sovVal = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  return isMoneyField(field) ? fmt$(v) : String(v);
};

const FieldPill = ({ field }) => {
  const money = isMoneyField(field);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: '0.02em',
        color: money ? 'var(--warning)' : 'var(--text-3)',
        background: money ? 'var(--warning-soft)' : 'var(--bg-surface-2)',
        border: money ? '1px solid color-mix(in oklch, var(--warning) 22%, transparent)' : '1px solid var(--border-default)',
        whiteSpace: 'nowrap',
      }}
    >
      {prettyField(field)}
    </span>
  );
};

const SovChangesTable = ({ changes }) => {
  const search = usePageSearch('Search SOV changes…');
  if (!changes.length) {
    return (
      <StateScreen
        icon="list"
        title="No SOV line item changes recorded yet"
        body="Double-click any SOV item number to edit its properties. Every line-item edit will be logged here."
      />
    );
  }
  const head = [
    { label: 'Date' },
    { label: 'SOV Code' },
    { label: 'Description' },
    { label: 'Field' },
    { label: 'Old Value' },
    { label: 'New Value' },
    { label: 'Reason' },
  ];
  const shownChanges = changes.filter((c) => matchesSearch(search, c.sov_code, c.description, prettyField(c.field_changed),
    c.old_value, c.new_value, c.change_reason));
  return (
    <TableCard template={SOV_TEMPLATE} head={head}>
      {shownChanges.length === 0 && <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 14, color: 'var(--text-3)' }}>{noMatchText(search).body}</div>}
      {shownChanges.map((c) => {
        const money = isMoneyField(c.field_changed);
        return (
          <div
            key={c.id}
            className={money ? 'cv-row-money' : 'cv-row'}
            style={{
              display: 'grid',
              gridTemplateColumns: SOV_TEMPLATE,
              minHeight: 56,
              borderBottom: '1px solid var(--border-subtle)',
              background: money ? 'var(--warning-soft)' : 'transparent',
            }}
          >
            <Cell>
              <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
                {fmtDate(c.changed_at)}
              </span>
            </Cell>
            <Cell>
              <span className="mono" style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--accent-text)', fontVariantNumeric: 'tabular-nums' }}>
                {c.sov_code}
              </span>
            </Cell>
            <Cell>
              <span style={{ fontSize: 14, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 12 }}>
                {c.description}
              </span>
            </Cell>
            <Cell>
              <FieldPill field={c.field_changed} />
            </Cell>
            <Cell>
              <span className={money ? 'mono' : undefined} style={{ fontSize: 13.5, color: 'var(--danger)', fontVariantNumeric: money ? 'tabular-nums' : 'normal' }}>
                {sovVal(c.field_changed, c.old_value)}
              </span>
            </Cell>
            <Cell>
              <span className={money ? 'mono' : undefined} style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--success)', fontVariantNumeric: money ? 'tabular-nums' : 'normal' }}>
                {sovVal(c.field_changed, c.new_value)}
              </span>
            </Cell>
            <Cell>
              {c.change_reason ? (
                <span style={{ fontSize: 13.5, color: 'var(--warning)', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.change_reason}
                </span>
              ) : (
                <span style={{ fontSize: 13.5, color: 'var(--text-4)' }}>—</span>
              )}
            </Cell>
          </div>
        );
      })}
    </TableCard>
  );
};

/* ─────────────── Modal shell + form fields ─────────────── */

const ModalShell = ({ open, onClose, title, subtitle, width = 520, disableClose, children, footer }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !disableClose) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, disableClose]);
  if (!open) return null;
  return (
    <div
      className="modal-overlay"
      onClick={() => !disableClose && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'oklch(0.2 0.008 100 / 0.32)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 'max(60px, 8vh)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 96px)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-surface)',
          borderRadius: 14,
          border: '1px solid var(--border-default)',
          boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.22)',
          overflow: 'hidden',
          fontFamily: 'var(--font-sans)',
          color: 'var(--text-1)',
          letterSpacing: '-0.005em',
        }}
      >
        <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.012em' }}>{title}</h2>
            {subtitle && <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-3)', lineHeight: 1.5 }}>{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={() => !disableClose && onClose()}
            disabled={disableClose}
            aria-label="Close"
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              color: 'var(--text-3)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: '1px solid transparent',
              cursor: disableClose ? 'not-allowed' : 'pointer',
              flex: '0 0 auto',
              opacity: disableClose ? 0.4 : 1,
            }}
          >
            <Icon name="x" size={14} />
          </button>
        </header>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 20px' }}>{children}</div>
        {footer && (
          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 8,
              padding: '12px 20px 16px',
              borderTop: '1px solid var(--border-subtle)',
              background: 'var(--bg-surface-2)',
            }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
};

const FieldLabel = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', letterSpacing: '0.01em', marginBottom: 6 }}>
    {children}
    {required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
  </label>
);

const focusRing = (focused) => ({
  border: focused ? '1px solid var(--accent)' : '1px solid var(--border-default)',
  boxShadow: focused ? '0 0 0 3px var(--accent-ring)' : 'none',
});

const TextInput = ({ value, onChange, placeholder, mono, autoFocus }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: 36, padding: '0 12px', background: 'var(--bg-surface)', borderRadius: 8, ...focusRing(focused) }}>
      <input
        type="text"
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        className={mono ? 'mono' : undefined}
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: 14.5,
          color: 'var(--text-1)',
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)',
          letterSpacing: mono ? '-0.02em' : '-0.005em',
          fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
        }}
      />
    </div>
  );
};

const MoneyInput = ({ value, onChange, placeholder = '0.00', sign, autoFocus }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 12px', background: 'var(--bg-surface)', borderRadius: 8, ...focusRing(focused) }}>
      <span style={{ fontSize: 14.5, color: 'var(--text-4)', flex: '0 0 auto' }}>$</span>
      <input
        type="number"
        step="0.01"
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        className="mono"
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: 14.5,
          color: 'var(--text-1)',
          fontFamily: 'var(--font-mono)',
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
        }}
      />
      {sign && <span style={{ fontSize: 13, color: 'var(--text-4)', flex: '0 0 auto' }}>+ / −</span>}
    </div>
  );
};

const TextAreaInput = ({ value, onChange, placeholder, rows = 3 }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ padding: '8px 12px', background: 'var(--bg-surface)', borderRadius: 8, ...focusRing(focused) }}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: '100%',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          resize: 'vertical',
          fontSize: 14.5,
          color: 'var(--text-1)',
          fontFamily: 'var(--font-sans)',
          lineHeight: 1.5,
          letterSpacing: '-0.005em',
        }}
      />
    </div>
  );
};

const SubmitBtn = ({ children, icon, tone = 'accent', busy, onClick, type = 'button' }) => {
  const bg = tone === 'success' ? 'var(--success)' : tone === 'warning' ? 'var(--warning)' : 'var(--accent)';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={busy}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 34,
        padding: '0 14px',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 500,
        color: '#fff',
        background: bg,
        border: `1px solid color-mix(in oklch, ${bg} 80%, oklch(0.2 0.02 135))`,
        boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.18)',
        cursor: busy ? 'default' : 'pointer',
        opacity: busy ? 0.85 : 1,
      }}
    >
      {busy ? <Icon name="spin" size={13} sw={2.4} /> : icon && <Icon name={icon} size={13} sw={2.2} />}
      {children}
    </button>
  );
};

const ModalSummaryRow = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
    <span style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</span>
    <span style={{ fontSize: 14.5, textAlign: 'right' }}>{children}</span>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const SECTION_OPTIONS = [
  { value: 'overview', label: 'Overview' },
  { value: 'cos', label: 'Change Orders' },
  { value: 'audit', label: 'Audit Log' },
  { value: 'sov', label: 'SOV Changes' },
];

const ContractValueTab = ({ projectId, refreshSummary }) => {
  const { confirm, confirmDialogProps } = useConfirm();
  const [summary, setSummary] = useState(null);
  const [log, setLog] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [sovChangeLog, setSovChangeLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  // Change Order form
  const [showCOModal, setShowCOModal] = useState(false);
  const [coForm, setCOForm] = useState({ change_order_number: '', title: '', description: '', requested_amount: '', notes: '' });

  // Approve modal
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedCO, setSelectedCO] = useState(null);
  const [approvedAmount, setApprovedAmount] = useState('');

  // Manual adjustment form
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ description: '', amount_change: '', changed_by: '' });

  // Double-submit guards (per-action so an in-flight request doesn't freeze unrelated rows)
  const [creatingCO, setCreatingCO] = useState(false);
  const [approving, setApproving] = useState(false);
  const [statusChangingId, setStatusChangingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [adjusting, setAdjusting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [summaryData, logData, coData, sovLogData] = await Promise.all([
        getContractValueSummary(projectId),
        getContractValueLog(projectId),
        getChangeOrders(projectId),
        getSOVProjectChangeLog(projectId),
      ]);
      setSummary(summaryData);
      setLog(logData);
      setChangeOrders(coData);
      setSovChangeLog(sovLogData);
    } catch (error) {
      console.error('Error fetching contract value data:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleCreateCO = async (e) => {
    if (e) e.preventDefault();
    if (creatingCO) return;
    try {
      setCreatingCO(true);
      await createChangeOrder({ ...coForm, project_id: projectId, status: 'Draft' });
      setCOForm({ change_order_number: '', title: '', description: '', requested_amount: '', notes: '' });
      setShowCOModal(false);
      await fetchAll();
      if (refreshSummary) refreshSummary();
    } catch (error) {
      console.error('Error creating change order:', error);
    } finally {
      setCreatingCO(false);
    }
  };

  const handleStatusChange = async (co, newStatus) => {
    if (newStatus === 'Approved') {
      setSelectedCO(co);
      setApprovedAmount(co.requested_amount || '');
      setShowApproveModal(true);
      return;
    }
    if (statusChangingId) return;
    try {
      setStatusChangingId(co.id);
      await updateChangeOrder(co.id, { status: newStatus });
      await fetchAll();
      if (refreshSummary) refreshSummary();
    } catch (error) {
      console.error('Error updating change order:', error);
    } finally {
      setStatusChangingId(null);
    }
  };

  const handleApprove = async () => {
    if (approving) return;
    try {
      setApproving(true);
      await updateChangeOrder(selectedCO.id, { status: 'Approved', approved_amount: parseFloat(approvedAmount) });
      setShowApproveModal(false);
      setSelectedCO(null);
      setApprovedAmount('');
      await fetchAll();
      if (refreshSummary) refreshSummary();
    } catch (error) {
      console.error('Error approving change order:', error);
    } finally {
      setApproving(false);
    }
  };

  const handleDeleteCO = async (co) => {
    if (deletingId) return;
    const ok = await confirm({
      title: 'Delete Change Order',
      message: 'Are you sure you want to delete this change order? This action cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      setDeletingId(co.id);
      await deleteChangeOrder(co.id);
      await fetchAll();
      if (refreshSummary) refreshSummary();
    } catch (error) {
      console.error('Error deleting change order:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleManualAdjustment = async (e) => {
    if (e) e.preventDefault();
    if (adjusting) return;
    try {
      setAdjusting(true);
      await createManualAdjustment({
        project_id: projectId,
        description: adjustForm.description,
        amount_change: parseFloat(adjustForm.amount_change),
        changed_by: adjustForm.changed_by,
      });
      setAdjustForm({ description: '', amount_change: '', changed_by: '' });
      setShowAdjustModal(false);
      await fetchAll();
      if (refreshSummary) refreshSummary();
    } catch (error) {
      console.error('Error creating manual adjustment:', error);
    } finally {
      setAdjusting(false);
    }
  };

  const { approvedCOs, approvedCount, pendingCount, pendingTotal } = useMemo(() => {
    const isStatus = (co, s) => String(co.status || '').toLowerCase() === s;
    const approved = changeOrders.filter((co) => isStatus(co, 'approved'));
    const pending = changeOrders.filter((co) => isStatus(co, 'draft') || isStatus(co, 'submitted'));
    return {
      approvedCOs: approved,
      approvedCount: approved.length,
      pendingCount: pending.length,
      pendingTotal: pending.reduce((s, co) => s + (+co.requested_amount || 0), 0),
    };
  }, [changeOrders]);

  if (loading) return <LoadingState message="Loading contract value…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes cvSpin { to { transform: rotate(360deg); } }
        .cv-primary-btn:hover { background: var(--accent-hover) !important; }
        .cv-ghost-btn:hover {
          border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)) !important;
          color: var(--accent-text) !important;
          background: var(--accent-soft) !important;
        }
        .cv-row:hover { background: var(--bg-hover); }
        .cv-row-money:hover { background: color-mix(in oklch, var(--warning) 14%, var(--bg-surface)); }
      `}</style>

      <SectionHeader onManualAdjust={() => setShowAdjustModal(true)} onNewCO={() => setShowCOModal(true)} />

      {summary && (
        <KpiRow summary={summary} approvedCOs={approvedCOs} approvedCount={approvedCount} pendingCount={pendingCount} pendingTotal={pendingTotal} />
      )}

      <SegmentedControl value={activeSection} onChange={setActiveSection} options={SECTION_OPTIONS} />

      {activeSection === 'overview' && summary && <Overview summary={summary} approvedCOs={approvedCOs} />}

      {activeSection === 'cos' && (
        <ChangeOrdersTable
          changeOrders={changeOrders}
          onNewCO={() => setShowCOModal(true)}
          statusChangingId={statusChangingId}
          deletingId={deletingId}
          onSubmit={(co) => handleStatusChange(co, 'Submitted')}
          onDelete={(co) => handleDeleteCO(co)}
          onApprove={(co) => handleStatusChange(co, 'Approved')}
          onReject={(co) => handleStatusChange(co, 'Rejected')}
        />
      )}

      {activeSection === 'audit' && <AuditLogTable entries={log} />}

      {activeSection === 'sov' && <SovChangesTable changes={sovChangeLog} />}

      {/* ─────────────── New Change Order modal ─────────────── */}
      <ModalShell
        open={showCOModal}
        onClose={() => setShowCOModal(false)}
        title="New Change Order"
        subtitle="Create a change order. It will affect the contract value once approved."
        width={560}
        disableClose={creatingCO}
        footer={
          <>
            <button type="button" style={bGhost} onClick={() => setShowCOModal(false)} disabled={creatingCO}>
              Cancel
            </button>
            <SubmitBtn icon="plus" tone="accent" busy={creatingCO} onClick={handleCreateCO}>
              {creatingCO ? 'Creating…' : 'Create Change Order'}
            </SubmitBtn>
          </>
        }
      >
        <form autoComplete="off" onSubmit={handleCreateCO} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <FieldLabel required>CO Number</FieldLabel>
              <TextInput value={coForm.change_order_number} onChange={(v) => setCOForm({ ...coForm, change_order_number: v })} placeholder="e.g. CO-001" mono autoFocus />
            </div>
            <div>
              <FieldLabel required>Requested Amount</FieldLabel>
              <MoneyInput value={coForm.requested_amount} onChange={(v) => setCOForm({ ...coForm, requested_amount: v })} placeholder="0.00" />
            </div>
          </div>
          <div>
            <FieldLabel required>Title</FieldLabel>
            <TextInput value={coForm.title} onChange={(v) => setCOForm({ ...coForm, title: v })} placeholder="Short descriptive title" />
          </div>
          <div>
            <FieldLabel>Description</FieldLabel>
            <TextAreaInput value={coForm.description} onChange={(v) => setCOForm({ ...coForm, description: v })} placeholder="What scope does this change order cover?" rows={3} />
          </div>
          <div>
            <FieldLabel>Notes</FieldLabel>
            <TextAreaInput value={coForm.notes} onChange={(v) => setCOForm({ ...coForm, notes: v })} placeholder="Internal notes (optional)" rows={2} />
          </div>
          {/* hidden submit keeps Enter working without a visible native button */}
          <button type="submit" style={{ display: 'none' }} aria-hidden tabIndex={-1} />
        </form>
      </ModalShell>

      {/* ─────────────── Approve modal ─────────────── */}
      <ModalShell
        open={showApproveModal && !!selectedCO}
        onClose={() => setShowApproveModal(false)}
        title="Approve Change Order"
        subtitle="This will add the approved amount to the contract value and create a new SOV line."
        width={520}
        disableClose={approving}
        footer={
          <>
            <button type="button" style={bGhost} onClick={() => setShowApproveModal(false)} disabled={approving}>
              Cancel
            </button>
            <SubmitBtn icon="check" tone="success" busy={approving} onClick={handleApprove}>
              {approving ? 'Approving…' : 'Approve & Add to SOV'}
            </SubmitBtn>
          </>
        }
      >
        {selectedCO && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                padding: '14px 16px',
                borderRadius: 10,
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <ModalSummaryRow label="Change Order">
                <span className="mono" style={{ fontWeight: 500, color: 'var(--accent-text)' }}>
                  CO {selectedCO.change_order_number}
                </span>
              </ModalSummaryRow>
              <ModalSummaryRow label="Title">
                <span style={{ color: 'var(--text-1)' }}>{selectedCO.title}</span>
              </ModalSummaryRow>
              <ModalSummaryRow label="Requested Amount">
                <span className="mono" style={{ fontWeight: 600, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>
                  {fmt$(selectedCO.requested_amount, { cents: true })}
                </span>
              </ModalSummaryRow>
            </div>
            <div>
              <FieldLabel required>Approved Amount</FieldLabel>
              <MoneyInput value={approvedAmount} onChange={setApprovedAmount} placeholder="0.00" autoFocus />
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-3)' }}>
                Pre-filled with the requested amount. Adjust if the approved value differs.
              </p>
            </div>
          </div>
        )}
      </ModalShell>

      {/* ─────────────── Manual Adjustment modal ─────────────── */}
      <ModalShell
        open={showAdjustModal}
        onClose={() => setShowAdjustModal(false)}
        title="Manual Adjustment"
        subtitle="Manually adjust the contract value. Use negative amounts to decrease."
        width={520}
        disableClose={adjusting}
        footer={
          <>
            <button type="button" style={bGhost} onClick={() => setShowAdjustModal(false)} disabled={adjusting}>
              Cancel
            </button>
            <SubmitBtn icon="sliders" tone="warning" busy={adjusting} onClick={handleManualAdjustment}>
              {adjusting ? 'Recording…' : 'Record Adjustment'}
            </SubmitBtn>
          </>
        }
      >
        <form autoComplete="off" onSubmit={handleManualAdjustment} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <FieldLabel required>Description</FieldLabel>
            <TextInput value={adjustForm.description} onChange={(v) => setAdjustForm({ ...adjustForm, description: v })} placeholder="Reason for this adjustment" autoFocus />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <FieldLabel required>Amount (+/−)</FieldLabel>
              <MoneyInput value={adjustForm.amount_change} onChange={(v) => setAdjustForm({ ...adjustForm, amount_change: v })} placeholder="0.00" sign />
            </div>
            <div>
              <FieldLabel>Changed By</FieldLabel>
              <TextInput value={adjustForm.changed_by} onChange={(v) => setAdjustForm({ ...adjustForm, changed_by: v })} placeholder="Your name" />
            </div>
          </div>
          <button type="submit" style={{ display: 'none' }} aria-hidden tabIndex={-1} />
        </form>
      </ModalShell>

      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default ContractValueTab;
